import React, { memo, useState, useEffect, useMemo, useRef } from 'react';
import type { StepNode } from '@segmently/schema-models/v2';
import type { OnActionCallback, OnEventCallback } from '../../../types/events';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { createSliderMoveEvent, createSliderCycleCompleteEvent } from '../../../utils/eventEmitter';

import { Slider } from '../../shared/Slider';
import { Background } from '../../shared/Background';
import { PageIndicator } from '../../shared/PageIndicator';
import { DashesPageIndicator } from '../../shared/DashesPageIndicator';
import { normalizeSlideItems } from '../../../utils/normalizeCollections';
import styles from './Carousel.module.css';

const TOTAL_DURATION_PERCENT = 100;

function distributeEvenly(count: number): number[] {
  if (count <= 0) return [];
  if (count === 1) return [TOTAL_DURATION_PERCENT];

  const base = Math.floor(TOTAL_DURATION_PERCENT / count);
  const remainder = TOTAL_DURATION_PERCENT - base * count;
  return Array.from({ length: count }, (_, index) => base + (index < remainder ? 1 : 0));
}

function resolveSlideDurationPercents(
  items: Array<{ progressStart?: number; progressEnd?: number }>,
): number[] {
  if (items.length === 0) return [];
  if (items.length === 1) return [TOTAL_DURATION_PERCENT];

  const durations = items.map((item) => {
    const start = item.progressStart;
    const end = item.progressEnd;
    if (typeof start !== 'number' || typeof end !== 'number') return 0;
    if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
    return Math.max(0, end - start);
  });

  const total = durations.reduce((sum, duration) => sum + duration, 0);
  const hasValidDistribution = durations.every((duration) => duration > 0)
    && Math.abs(total - TOTAL_DURATION_PERCENT) < 1;

  return hasValidDistribution ? durations : distributeEvenly(items.length);
}

export interface CarouselProps {
  step: StepNode;
  locale: string;
  variables?: TemplateVariables;
  resolvedScreenValues?: Record<string, string>;
  onAction?: OnActionCallback;
  onEvent?: OnEventCallback;
  editMode?: boolean;
  previewMode?: PreviewMode;
  isAnimationPlaying?: boolean;
  onAnimationComplete?: () => void;
}

export const Carousel = memo(function Carousel({
  step,
  locale,
  variables,
  resolvedScreenValues,
  onAction,
  onEvent,
  editMode = false,
  previewMode,
  isAnimationPlaying = false,
  onAnimationComplete,
}: CarouselProps): React.ReactElement {
  const { content } = step;

  const [sliderIndex, setSliderIndex] = useState(0);
  const wasControlledPlayingRef = useRef(false);
  const hasCompletedControlledRunRef = useRef(false);

  const carousel = content?.carousel;
  const rawItems = carousel?.items;
  const items = useMemo(() => normalizeSlideItems(rawItems), [rawItems]);
  const normalizedCarousel = useMemo(
    () => (carousel ? { ...carousel, items } : undefined),
    [carousel, items],
  );
  const timerDuration = content?.carousel?.timer?.duration ?? 3;
  const slideDurationPercents = useMemo(() => resolveSlideDurationPercents(items), [items]);
  const shouldAutoStart = !editMode && previewMode !== 'static';

  useEffect(() => {
    if (previewMode === 'static' || !editMode) return;

    if (isAnimationPlaying && !wasControlledPlayingRef.current) {
      setSliderIndex(0);
      hasCompletedControlledRunRef.current = false;
    }

    if (!isAnimationPlaying) {
      hasCompletedControlledRunRef.current = false;
    }

    wasControlledPlayingRef.current = isAnimationPlaying;
  }, [editMode, isAnimationPlaying, previewMode]);

  // Timer-based auto-advance
  useEffect(() => {
    if (previewMode === 'static') return;
    if (items.length === 0) return;
    if (!shouldAutoStart && (!editMode || !isAnimationPlaying || hasCompletedControlledRunRef.current)) return;

    const durationPercent = slideDurationPercents[sliderIndex] ?? (TOTAL_DURATION_PERCENT / items.length);

    const slideTimeMs = Math.max(100, (timerDuration * 1000 * durationPercent) / 100);

    const timer = setTimeout(() => {
      const isLastSlide = sliderIndex >= items.length - 1;

      if (isLastSlide) {
        if (onAction) {
          onAction(createSliderCycleCompleteEvent('slider-cycle-complete', step, 'slider'));
        }

        if (editMode) {
          hasCompletedControlledRunRef.current = true;
          onAnimationComplete?.();
          return;
        }

        if (items.length > 1) {
          setSliderIndex(0);
        }
        return;
      }

      if (items.length > 1) {
        setSliderIndex(prev => Math.min(prev + 1, items.length - 1));
      }
    }, slideTimeMs);

    return () => clearTimeout(timer);
  }, [
    editMode,
    isAnimationPlaying,
    items,
    onAction,
    onAnimationComplete,
    previewMode,
    shouldAutoStart,
    sliderIndex,
    slideDurationPercents,
    step,
    timerDuration,
  ]);

  // Emit slider-move event when index changes
  useEffect(() => {
    if (onEvent && items.length > 0) {
      onEvent(createSliderMoveEvent('slider-move', step, `slider-item-${sliderIndex}`));
    }
  }, [sliderIndex, step, onEvent, items.length]);

  const ep = editMode || previewMode === 'live' ? 'content' : undefined;

  return (
    <div
      className={editMode ? `${styles.container} screen-view--edit-mode` : styles.container}
      data-testid="view-carousel"
    >
      <Background
        backdrop={content?.canvas?.background}
        locale={locale}
        editPath={ep ? `${ep}.canvas.background` : undefined}
        previewMode={previewMode}
      />

      <Slider
        carousel={normalizedCarousel}
        locale={locale}
        currentIndex={sliderIndex}
        editPath={ep ? `${ep}.carousel` : undefined}
        previewMode={previewMode}
        variables={variables}
        resolvedScreenValues={resolvedScreenValues}
      />

      {/* Optional progress indicator overlay */}
      {content?.carousel?.progressIndicator && (
        <div className={styles.progressContainer}>
          {(() => {
            const pi = content.carousel.progressIndicator;
            const kind = pi.kind || 'linearBar';
            const isDashes = kind === 'segmented' || kind === 'segmentedTitle' || kind === 'segmentedIcon' || kind === 'segmentedTitleIcon';
            if (isDashes) {
              return (
                <DashesPageIndicator
                  indicator={pi}
                  data-testid="view-carousel-page-indicator"
                  editPath={ep ? `${ep}.carousel.progressIndicator` : undefined}
                />
              );
            }
            return (
              <PageIndicator
                indicator={pi}
                data-testid="view-carousel-page-indicator"
                editPath={ep ? `${ep}.carousel.progressIndicator` : undefined}
              />
            );
          })()}
        </div>
      )}
    </div>
  );
});

Carousel.displayName = 'Carousel';
